import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { CandidateNomination, EligibilityCheckCode, EligibilityCheckResult } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, Modal, PageHeader, StatCard, StatusBadge } from '../../ui/components';

const DECISION_ROLES = ['REGISTRATION_OFFICER', 'ELECTORAL_COMMISSIONER'];

/** GET /nominations/:id — includes eligibilityChecks. Attest/reject are decisionMaker-only; withdraw is self or officer. */
export function NominationDetail() {
  const { id } = useParams<{ id: string }>();
  const { persona } = useAuth();
  const isDecisionMaker = persona?.roles.some((r) => DECISION_ROLES.includes(r)) ?? false;
  const nomQ = useApiQuery<CandidateNomination>(id ? `/api/v1/m39/nominations/${id}` : null);
  const [attestCheck, setAttestCheck] = useState<EligibilityCheckCode | null>(null);
  const [showReject, setShowReject] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  if (nomQ.loading) return <div className="px-4 py-8 text-sm text-slate-500">Loading nomination…</div>;
  if (nomQ.error) return <ErrorNote message={nomQ.error} />;
  const n = nomQ.data;
  if (!n) return null;

  const isSelf = persona?.nin && persona.nin === n.candidateNin;
  const canWithdraw = isSelf || isDecisionMaker;
  const latestPerCheck = new Map<string, EligibilityCheckResult>();
  for (const c of n.eligibilityChecks ?? []) {
    const cur = latestPerCheck.get(c.checkCode);
    if (!cur || c.checkedAt >= cur.checkedAt) latestPerCheck.set(c.checkCode, c);
  }

  return (
    <div>
      <PageHeader
        title={n.candidateName}
        subtitle={`${n.seatLabel} · ${n.independent ? 'Independent' : `Party-affiliated`} · NIN ${n.candidateNin}`}
        actions={<StatusBadge status={n.status} />}
      />

      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Deposit" value={scr(n.depositAmountScr)} hint={n.depositStatus} />
        <StatCard label="Submitted" value={formatDateTime(n.submittedAt)} />
        <StatCard label="Papers SHA-256" value={<span className="font-mono text-xs">{n.papersSha256.slice(0, 16)}…</span>} />
        <StatCard label="Certificate" value={n.certificateDocSerial ?? 'Not yet issued'} />
      </div>

      <div className="card mb-6 p-5">
        <h2 className="mb-3 text-sm font-bold text-slate-700">Eligibility checks</h2>
        <div className="space-y-2">
          {(['CITIZENSHIP', 'MINIMUM_AGE', 'NOT_DECEASED', 'CRIMINAL_DISQUALIFICATION', 'BUSINESS_DISQUALIFICATION'] as EligibilityCheckCode[]).map((code) => {
            const c = latestPerCheck.get(code);
            return (
              <div key={code} className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                <div>
                  <div className="text-sm font-semibold text-slate-800">{code.replaceAll('_', ' ')}</div>
                  <div className="text-xs text-slate-500">{c?.detail ?? 'Not yet checked'}</div>
                  <div className="text-[11px] text-slate-400">by {c?.checkedBy ?? '—'}</div>
                </div>
                <div className="flex items-center gap-2">
                  {c ? <StatusBadge status={c.status} /> : null}
                  {isDecisionMaker && n.status === 'ELIGIBILITY_CHECK' && (c?.status === 'NEEDS_REVIEW' || c?.status === 'UNAVAILABLE') ? (
                    <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setAttestCheck(code)}>
                      Attest
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {n.rejectionReason ? (
        <div className="card mb-6 border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">Rejected: {n.rejectionReason}</div>
      ) : null}
      {n.withdrawnReason ? (
        <div className="card mb-6 border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">Withdrawn: {n.withdrawnReason}</div>
      ) : null}
      {n.certificateVerifyUrl ? (
        <div className="card mb-6 border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
          Certificate of Nomination issued — verify at{' '}
          <a href={n.certificateVerifyUrl} className="underline" target="_blank" rel="noreferrer">
            {n.certificateVerifyUrl}
          </a>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2">
        {isDecisionMaker && (['SUBMITTED', 'ELIGIBILITY_CHECK', 'DEPOSIT_PENDING'] as string[]).includes(n.status) ? (
          <button className="btn-danger" onClick={() => setShowReject(true)}>
            Reject nomination
          </button>
        ) : null}
        {canWithdraw && (['SUBMITTED', 'ELIGIBILITY_CHECK', 'DEPOSIT_PENDING', 'ACCEPTED'] as string[]).includes(n.status) ? (
          <button className="btn-secondary" onClick={() => setShowWithdraw(true)}>
            Withdraw nomination
          </button>
        ) : null}
      </div>
      {n.depositStatus === 'PENDING' && n.status === 'DEPOSIT_PENDING' ? (
        <p className="mt-3 text-xs text-slate-400">
          Deposit intent {n.depositPayIntentRefNo ?? '—'} awaiting payment — confirmation is a SeyPay webhook
          (POST /nominations/:id/deposit/confirm, service-token only), not a staff action.
        </p>
      ) : null}

      {attestCheck ? (
        <AttestModal
          nominationId={n.id}
          code={attestCheck}
          onClose={() => setAttestCheck(null)}
          onDone={() => {
            setAttestCheck(null);
            nomQ.reload();
          }}
        />
      ) : null}
      {showReject ? (
        <ReasonModal
          title="Reject nomination"
          danger
          busy={busy}
          onClose={() => setShowReject(false)}
          onSubmit={async (reason) => {
            setBusy(true);
            setActionError(null);
            try {
              await api.post(`/api/v1/m39/nominations/${n.id}/reject`, { reason });
              setShowReject(false);
              nomQ.reload();
            } catch (e) {
              setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Rejection failed');
            } finally {
              setBusy(false);
            }
          }}
        />
      ) : null}
      {showWithdraw ? (
        <ReasonModal
          title="Withdraw nomination"
          busy={busy}
          onClose={() => setShowWithdraw(false)}
          onSubmit={async (reason) => {
            setBusy(true);
            setActionError(null);
            try {
              await api.post(`/api/v1/m39/nominations/${n.id}/withdraw`, { reason });
              setShowWithdraw(false);
              nomQ.reload();
            } catch (e) {
              setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Withdrawal failed');
            } finally {
              setBusy(false);
            }
          }}
        />
      ) : null}
    </div>
  );
}

function AttestModal({ nominationId, code, onClose, onDone }: { nominationId: string; code: EligibilityCheckCode; onClose: () => void; onDone: () => void }) {
  const [status, setStatus] = useState<'PASS' | 'FAIL'>('PASS');
  const [detail, setDetail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m39/nominations/${nominationId}/checks/${code}/attest`, { status, detail });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Attestation failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Attest — ${code.replaceAll('_', ' ')}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Verdict">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value as 'PASS' | 'FAIL')}>
            <option value="PASS">Pass</option>
            <option value="FAIL">Fail</option>
          </select>
        </Field>
        <Field label="Detail (your reasoning, for the audit record)">
          <input className="field-input" value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="e.g. Reviewed CRO disposition — spent conviction, not disqualifying" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || detail.trim().length < 3}>
            {busy ? 'Attesting…' : 'Submit attestation'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ReasonModal({
  title,
  danger,
  busy,
  onClose,
  onSubmit,
}: {
  title: string;
  danger?: boolean;
  busy: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');
  return (
    <Modal title={title} onClose={onClose}>
      <div className="space-y-3">
        <Field label="Reason">
          <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className={danger ? 'btn-danger' : 'btn-primary'} onClick={() => onSubmit(reason)} disabled={busy || reason.trim().length < 3}>
            {busy ? 'Working…' : 'Confirm'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
